<template>
  <div class="account-products-tab">
    <div v-if="loading" class="account-products-tab__loading">Loading products…</div>
    <div v-else class="account-products-tab__list">
      <div v-for="item in productRows" :key="item.key" class="account-products-tab__row">
        <div class="account-products-tab__meta">
          <span class="account-products-tab__name">{{ item.label }}</span>
          <span v-if="item.description" class="account-products-tab__description">{{
            item.description
          }}</span>
          <span v-if="item.hint" class="account-products-tab__hint">{{ item.hint }}</span>
        </div>
        <EXTooltip :title="item.tooltip" :disabled="!item.tooltip" placement="top" :delay="100">
          <div class="account-products-tab__switch-wrap">
            <SwitchButton
              :key="item.key === 'readiness' ? readinessSwitchKey : item.key"
              :label="item.label"
              :showTitle="false"
              :value="item.value"
              :disabled="item.disabled"
              :toggleLoader="item.key === savingKey"
              containerWidth="auto"
              left="0px"
              @input="onToggle(item, $event)"
            />
          </div>
        </EXTooltip>
      </div>
    </div>

    <ExActionsPopUp
      v-if="readinessConfirmOpen"
      :value="readinessConfirmOpen"
      headline="Turn off Readiness?"
      :info="READINESS_CONFIRM_MESSAGE"
      actionLabel="Turn off Readiness"
      cancelLabel="Cancel"
      :primary="true"
      width="480"
      @onAction="confirmReadinessOff"
      @input="cancelReadinessOff"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from "vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import { getAccount, setProductFlag } from "@/api";
import configService from "@/services/config";
import launchDarklyService from "@/services/launchdarkly";
import { identifyAdminUserFromStorage } from "@/utils/launchDarklyAdminContext";
import {
  LD_FLAG_IDEA_MDP_624,
  LD_FLAG_IDEA_MDP_625,
} from "@components/constants/launchDarklyFlagKeys";

const READINESS_KEY = "readiness";
const KNOWLEDGE_KEY = "knowledge";

/** Products a sysadmin can turn on and off today. Everything else is unreleased. */
const RELEASED_PRODUCTS = ["readiness", "fair_pay", "procedures", "performance"];

/** Map a row key to the account-setting field it reads and writes. */
const PRODUCT_FIELDS = {
  readiness: "product_readiness",
  knowledge: "product_knowledge",
  procedures: "product_procedures",
  performance: "product_performance",
  fair_pay: "product_fair_pay",
  engagement: "product_engagement",
};

const PRODUCT_DESCRIPTIONS = {
  readiness: "Track employee readiness with badges, checkpoints and inspections.",
  knowledge: "Versioned videos, articles and quizzes that checkpoints draw on.",
  procedures: "Documented procedures and tasks your teams follow.",
  performance: "Measure and review how employees perform.",
  fair_pay: "Pay plans, pay ranges and fair pay for every position.",
  engagement: "Engagement surveys that show how your people feel at work.",
};

/** Six rows in side-menu order (MDP-624). */
const PRODUCTS_V2_ROWS = [
  { key: "readiness", label: "Readiness" },
  { key: "knowledge", label: "Knowledge" },
  { key: "procedures", label: "Procedures" },
  { key: "performance", label: "Performance" },
  { key: "fair_pay", label: "Fair Pay" },
  { key: "engagement", label: "Engagement" },
];

/** Legacy four-row layout, kept as the off fallback behind idea-MDP-624. */
const PRODUCTS_V1_ROWS = [
  { key: "readiness", label: "Readiness" },
  { key: "fair_pay", label: "Fair Pay" },
  { key: "performance", label: "Performance" },
  { key: "procedures", label: "Procedures" },
];

const NOT_AVAILABLE_TOOLTIP = "Not yet available";
const READINESS_CONFIRM_MESSAGE =
  "Readiness-only screens will be hidden for this account. No data is deleted.";
const KNOWLEDGE_NEEDS_READINESS_TOOLTIP = "Turn on Readiness to use Knowledge.";
const READINESS_LOCKED_BY_KNOWLEDGE_TOOLTIP = "Turn off Knowledge first.";
/**
 * The at-least-one-product rejection. The backend ships
 * PRODUCT_FLAG_NO_PRODUCTS_ENABLED (ER-10464); ACCOUNT_AT_LEAST_ONE_PRODUCT is
 * the code ER-12062's description names, so both spellings show the same
 * message and roll the switch back.
 */
const AT_LEAST_ONE_PRODUCT_CODES = [
  "PRODUCT_FLAG_NO_PRODUCTS_ENABLED",
  "ACCOUNT_AT_LEAST_ONE_PRODUCT",
];
const KNOWLEDGE_REQUIRES_READINESS_CODE = "PRODUCT_FLAG_KNOWLEDGE_REQUIRES_READINESS";
const GENERIC_SAVE_ERROR = "Failed to update product settings";

const PRODUCT_LABELS = {
  readiness: "Readiness",
  knowledge: "Knowledge",
  procedures: "Procedures",
  performance: "Performance",
  fair_pay: "Fair Pay",
  engagement: "Engagement",
};

/** Same wording as backend chatter note, using saved flags from the API response. */
function productToastMessageFromResponse(data, key) {
  const label = PRODUCT_LABELS[key] || key;
  const enabled = !!data?.[PRODUCT_FIELDS[key]];
  return `${label} ${enabled ? "enabled" : "disabled"}.`;
}

const PRODUCTS_TAB_BODY_CLASS = "account-products-tab--active";

const props = defineProps({
  accountId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(["flags-updated"]);

const app = getCurrentInstance();
const { proxy } = app || {};

const loading = ref(true);
const saving = ref(false);
const savingKey = ref(null);
const readinessConfirmOpen = ref(false);
/** Remount Readiness switch so v-switch resyncs when the confirm dialog is dismissed. */
const readinessSwitchKey = ref(0);
/** idea-MDP-624 — six rows, descriptions and the released-product rule. Kill switch (default on). */
const productsTabV2Enabled = ref(true);
/** idea-MDP-625 — Knowledge is a released product. Kill switch (default on). */
const knowledgeReleased = ref(true);
const flags = reactive({
  product_readiness: true,
  product_knowledge: false,
  product_procedures: false,
  product_performance: false,
  product_fair_pay: false,
  product_engagement: false,
});

const atLeastOneMessage = computed(() =>
  productsTabV2Enabled.value
    ? "An account must have at least one released product on."
    : "An account must have at least one product on."
);

const releasedProducts = computed(() =>
  knowledgeReleased.value ? [...RELEASED_PRODUCTS, KNOWLEDGE_KEY] : RELEASED_PRODUCTS
);

/** Count released products that are on. */
const enabledProductCount = computed(
  () => releasedProducts.value.filter((key) => !!flags[PRODUCT_FIELDS[key]]).length
);

/** Phase 1: Knowledge needs Readiness, so each switch locks the other. Returns the tooltip. */
function knowledgeDependencyLock(key) {
  if (!knowledgeReleased.value) return "";
  if (key === KNOWLEDGE_KEY && !flags.product_readiness) return KNOWLEDGE_NEEDS_READINESS_TOOLTIP;
  if (key === READINESS_KEY && flags.product_knowledge)
    return READINESS_LOCKED_BY_KNOWLEDGE_TOOLTIP;
  return "";
}

/** True when the released product is on and turning it off would leave none on. */
function isLastEnabledProduct(key) {
  return (
    releasedProducts.value.includes(key) &&
    !!flags[PRODUCT_FIELDS[key]] &&
    enabledProductCount.value === 1
  );
}

const productRows = computed(() => {
  const source = productsTabV2Enabled.value ? PRODUCTS_V2_ROWS : PRODUCTS_V1_ROWS;
  return source.map(({ key, label }) => {
    const released = releasedProducts.value.includes(key);
    const dependencyLock = released ? knowledgeDependencyLock(key) : "";
    const lastEnabled = released && isLastEnabledProduct(key);
    const tooltip =
      dependencyLock ||
      (lastEnabled ? atLeastOneMessage.value : released ? "" : NOT_AVAILABLE_TOOLTIP);
    return {
      key,
      label,
      description: productsTabV2Enabled.value ? PRODUCT_DESCRIPTIONS[key] : "",
      value: !!flags[PRODUCT_FIELDS[key]],
      disabled: !released || lastEnabled || !!dependencyLock,
      tooltip,
      hint: released ? "" : NOT_AVAILABLE_TOOLTIP,
    };
  });
});

function applyAccountPayload(data = {}) {
  flags.product_readiness = data.product_readiness !== false;
  flags.product_knowledge = !!data.product_knowledge;
  flags.product_procedures = !!data.product_procedures;
  flags.product_performance = !!data.product_performance;
  flags.product_fair_pay = !!data.product_fair_pay;
  flags.product_engagement = !!data.product_engagement;
  emit("flags-updated", { ...flags });
}

async function loadFlags() {
  if (!props.accountId) return;
  loading.value = true;
  try {
    const res = await getAccount(props.accountId);
    applyAccountPayload(res?.data || {});
  } catch {
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error("Failed to load product settings");
    }
  } finally {
    loading.value = false;
  }
}

/** Pull the structured error code out of the several shapes DRF can return. */
function readErrorPayload(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) {
    return data.filter((item) => typeof item === "string").join(" ");
  }
  if (data && typeof data === "object") {
    const candidate = data.error ?? data.code ?? data.detail ?? data.message ?? "";
    return typeof candidate === "string" ? candidate : "";
  }
  return "";
}

function isProductEditable(key) {
  return releasedProducts.value.includes(key) && !knowledgeDependencyLock(key);
}

function onToggle(item, nextValue) {
  if (item.disabled || saving.value) return;
  if (!isProductEditable(item.key)) return;

  const field = PRODUCT_FIELDS[item.key];
  const next = !!nextValue;
  if (next === !!flags[field]) return;

  // Confirm before turning Readiness off. Cancelling leaves the switch on.
  if (item.key === READINESS_KEY && next === false) {
    readinessConfirmOpen.value = true;
    readinessSwitchKey.value += 1;
    return;
  }

  saveProduct(item.key, next);
}

function saveErrorMessage(key, payload) {
  if (AT_LEAST_ONE_PRODUCT_CODES.some((code) => payload.includes(code))) {
    return atLeastOneMessage.value;
  }
  if (payload.includes(KNOWLEDGE_REQUIRES_READINESS_CODE)) {
    return key === READINESS_KEY
      ? READINESS_LOCKED_BY_KNOWLEDGE_TOOLTIP
      : KNOWLEDGE_NEEDS_READINESS_TOOLTIP;
  }
  return payload || GENERIC_SAVE_ERROR;
}

async function saveProduct(key, value) {
  const field = PRODUCT_FIELDS[key];
  const previous = !!flags[field];
  flags[field] = value;
  saving.value = true;
  savingKey.value = key;
  try {
    const res = await setProductFlag(props.accountId, { product: key, value });
    applyAccountPayload(res?.data || { ...flags });
    if (proxy?.$notifications?.success) {
      const message = productToastMessageFromResponse(res?.data || {}, key);
      proxy.$notifications.success(message);
    }
  } catch (err) {
    // Keep the switch in sync with the server: roll the optimistic change back.
    flags[field] = previous;
    const payload = readErrorPayload(err);
    const message = saveErrorMessage(key, payload);
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error(message);
    }
  } finally {
    saving.value = false;
    savingKey.value = null;
  }
}

function confirmReadinessOff() {
  readinessConfirmOpen.value = false;
  saveProduct("readiness", false);
}

function cancelReadinessOff() {
  readinessConfirmOpen.value = false;
  readinessSwitchKey.value += 1;
}

function isFlagEnabled(value) {
  return value === true || value === "true" || value === 1;
}

async function refreshProductsTabFlag() {
  if (!proxy?.$getFlag) {
    return;
  }
  const sdkKey = configService.get("ldSdkKey");
  if (sdkKey && !launchDarklyService.isInitialized) {
    await launchDarklyService.initialize(null, true);
  }
  await identifyAdminUserFromStorage(proxy);
  const value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_624, true);
  productsTabV2Enabled.value = isFlagEnabled(value);
  // The legacy four-row layout has no Knowledge row, so it can't lock Readiness.
  knowledgeReleased.value =
    productsTabV2Enabled.value && isFlagEnabled(await proxy.$getFlag(LD_FLAG_IDEA_MDP_625, true));
}

watch(
  () => props.accountId,
  () => {
    loadFlags();
  }
);

onMounted(async () => {
  document.body.classList.add(PRODUCTS_TAB_BODY_CLASS);
  await refreshProductsTabFlag();
  loadFlags();
});

onUnmounted(() => {
  document.body.classList.remove(PRODUCTS_TAB_BODY_CLASS);
});
</script>

<style scoped>
.account-products-tab {
  padding: 16px 24px 24px;
}

.account-products-tab__loading {
  color: #667085;
  font-size: 14px;
  padding: 12px 0;
}

.account-products-tab__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 560px;
}

.account-products-tab__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf0;
}

.account-products-tab__row:last-child {
  border-bottom: none;
}

.account-products-tab__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-products-tab__name {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.account-products-tab__description {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
}

.account-products-tab__hint {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
}

.account-products-tab__switch-wrap {
  display: inline-flex;
}
</style>

<!-- Only while Products tab is mounted: full-width stack so short success titles don't shrink the toast -->
<style>
body.account-products-tab--active .toast-stack {
  width: 304px;
  max-width: calc(100vw - 48px);
}
</style>
